/**
 * The fixed-step loop: poll the pad, step the sim once per 1/120 s of
 * accumulated time (at most 8 steps; excess time is dropped and counted),
 * then render the interpolated state. Rendering blends the newest tick with
 * the one before it by the accumulator's remainder.
 */

import { DT } from "@lache/sim";

export const MAX_STEPS_PER_FRAME = 8;
/** Frame deltas above this (ms) are treated as a pause, not simulated. */
export const MAX_FRAME_DELTA_MS = 250;

export interface LoopCallbacks {
  /** Run one simulation tick. `now` is performance.now() at the poll. */
  tick: (now: number) => void;
  /** Render with interpolation factor alpha in [0, 1). */
  render: (alpha: number, dtSeconds: number, timestamp: number) => void;
  /** Called once per frame before ticking with the raw frame delta (ms). */
  frameStart?: (deltaMs: number, timestamp: number) => void;
  /** Called after the render with timing information. */
  frameEnd?: (tickMs: number, renderMs: number, droppedTicks: number) => void;
}

export class FixedStepLoop {
  private accumulator = 0;
  private last = -1;
  private running = false;
  private rafId = 0;
  /** Simulated ticks per frame, for the overlay. */
  stepsThisFrame = 0;
  paused = false;

  constructor(private readonly cb: LoopCallbacks) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = -1;
    this.rafId = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  /** When set, requestAnimationFrame is ignored and advance() drives frames on a synthetic clock. */
  private manual = false;
  private manualTime = 0;

  /** Switch between the display clock and a manual clock (deterministic capture). */
  setManual(v: boolean): void {
    if (this.manual === v) return;
    this.manual = v;
    if (v) {
      cancelAnimationFrame(this.rafId);
      this.manualTime = this.last >= 0 ? this.last : 0;
    } else if (this.running) {
      this.last = -1;
      this.rafId = requestAnimationFrame(this.frame);
    }
  }

  /** Run exactly one frame of `deltaMs` on the manual clock: tick the sim as needed, then render. */
  advance(deltaMs: number): void {
    if (!this.manual) return;
    this.manualTime += deltaMs;
    this.runFrame(this.manualTime);
  }

  private readonly frame = (timestamp: number): void => {
    if (!this.running || this.manual) return;
    this.rafId = requestAnimationFrame(this.frame);
    this.runFrame(timestamp);
  };

  private runFrame(timestamp: number): void {
    if (this.last < 0) this.last = timestamp;
    let delta = timestamp - this.last;
    this.last = timestamp;
    this.cb.frameStart?.(delta, timestamp);
    if (delta > MAX_FRAME_DELTA_MS) delta = MAX_FRAME_DELTA_MS;
    if (this.paused) delta = 0;
    this.accumulator += delta / 1000;

    let steps = 0;
    let dropped = 0;
    const t0 = performance.now();
    while (this.accumulator >= DT - 1e-9) {
      if (steps >= MAX_STEPS_PER_FRAME) {
        dropped = Math.floor(this.accumulator / DT);
        this.accumulator -= dropped * DT;
        break;
      }
      this.cb.tick(performance.now());
      this.accumulator -= DT;
      steps++;
    }
    if (this.accumulator < 0) this.accumulator = 0;
    const t1 = performance.now();
    this.stepsThisFrame = steps;
    const alpha = this.accumulator / DT;
    this.cb.render(alpha, delta / 1000, timestamp);
    const t2 = performance.now();
    this.cb.frameEnd?.(t1 - t0, t2 - t1, dropped);
  }
}
